import type {
  XrefPumps,
  Equipment,
  Correlations,
  XrefIntakes
} from "@/composables/forms/formTypes";
import {
  getOperatingThrustLoad,
  calculateSealOperatingHP,
  calculateTotalIntakeOperatingHP
} from "@/libs/calculations/pumps";
import {
  DEFAULT_ESP_PUMP_RPM,
  DEFAULT_HPS_PUMP_RPM,
  MAX_MOTOR_LOAD_RATIO
} from "@/libs/calculations/constants";
import { getMotorSpeedRPM } from "@/libs/calculations/motors";
import type {
  BasicData,
  FluidProperties
} from "@/libs/calculations/fluidProperties/d";
import type { FluidPropertiesContext } from "../../fluidProperties/d";
import type { LagrangeInterpolationMapWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";
import type { SealBearingDataForPumpCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { PumpData, PumpHydraulicCalculations } from "./types";
import { getFrequencyRatio } from "./basics";
import { getPumpHydraulicCalculations } from "./hydraulics";

export function processPumpCalculations(
  motor: NonNullable<Equipment>["r_motor"],
  xrefPumps: XrefPumps,
  xrefIntakes: XrefIntakes,
  pumpIntakePressurePSIA: number,
  pumpIntakeTemperatureF: number,
  pumpHz: number,
  hasCompressionPump: boolean,
  gasHeadRerateRatio: number,
  basicData: BasicData,
  fluidPropertiesAtPumpContext: FluidPropertiesContext,
  fluidPropertiesAtIntake: FluidProperties,
  correlations: Correlations,
  pumpWearBHPCorrectionRatio: number,
  pumpWearHeadCorrectionRatio: number,
  isScenarioEspShore: boolean,
  customFluidProperties: LagrangeInterpolationMapWithGroupings | null,
  custom_viscosities_properties: LagrangeInterpolationMapWithGroupings | null,
  bubblePointPressurePSIAOverride: number,
  dewPointPressurePSIAOverride: number,
  condensateApi: number,
  fluidViscosityCPOverride: number | null,
  topSealBearingData: SealBearingDataForPumpCalculations,
  bottomSealBearingData: SealBearingDataForPumpCalculations,
  rpmCorrection: number
): PumpData {
  // Iterate maximum of 5 times to narrow in on the operating RPM for pumps ONLY
  const motorNameplateHP = motor?.nameplate_horsepower || 0;
  const rpm = isScenarioEspShore ? DEFAULT_ESP_PUMP_RPM : DEFAULT_HPS_PUMP_RPM;
  const normalizedRpmCorrection =
    Number.isFinite(rpmCorrection) && rpmCorrection > 0 ? rpmCorrection : 1;
  let operatingRPM = rpm * (pumpHz / 60);
  let frequencyRatio = getFrequencyRatio(operatingRPM, isScenarioEspShore);
  let motorLoadRatio = 1;
  const primaryPumpId = xrefPumps.length
    ? xrefPumps[xrefPumps.length - 1].id
    : null;
  let individualPumpCalculations: PumpData["individualPumpCalculations"] = {};
  let requiredSealBrakeHP = 0;
  let requiredIntakeBrakeHP = 0;
  let requiredPumpBrakeHP = 0;

  for (let i = 0; i < 5; i++) {
    let currentFluidPropertiesContext = fluidPropertiesAtPumpContext;
    let intakePressurePSIA = pumpIntakePressurePSIA;
    let intakeTemperatureF = pumpIntakeTemperatureF;
    const hydraulicCalculations = xrefPumps.reduce<
      Record<string, PumpHydraulicCalculations>
    >((acc, xrefPump) => {
      const calculations = getPumpHydraulicCalculations(
        intakePressurePSIA,
        intakeTemperatureF,
        xrefPump,
        operatingRPM,
        gasHeadRerateRatio,
        basicData,
        currentFluidPropertiesContext,
        correlations,
        pumpWearBHPCorrectionRatio,
        pumpWearHeadCorrectionRatio,
        isScenarioEspShore,
        customFluidProperties,
        custom_viscosities_properties,
        bubblePointPressurePSIAOverride,
        dewPointPressurePSIAOverride,
        condensateApi,
        fluidViscosityCPOverride
      );

      intakePressurePSIA =
        calculations.dischargeStageCalculations?.dischargePressurePSIA || 0;
      intakeTemperatureF =
        calculations.dischargeStageCalculations?.dischargeTemperatureF || 0;

      currentFluidPropertiesContext =
        calculations.dischargeStageCalculations
          ?.dischargeFluidPropertiesContext || fluidPropertiesAtPumpContext;
      acc[xrefPump.id] = calculations;
      return acc;
    }, {});

    // Adds pump eff for each pump by getting average across all of the
    // pump's stages' efficiencies
    const efficiencyCalculations = xrefPumps.reduce<
      Record<string, { efficiencyRatio: number }>
    >((acc, xrefPump) => {
      const pumpHydraulicCalculations = hydraulicCalculations[xrefPump.id];

      const hasCalcs = !!pumpHydraulicCalculations.stageCalculations.length;
      acc[xrefPump.id] = {
        efficiencyRatio: hasCalcs
          ? pumpHydraulicCalculations.stageCalculations.reduce(
              (sum, { efficiency }) => sum + efficiency,
              0
            ) / pumpHydraulicCalculations.stageCalculations.length
          : 0
      };
      return acc;
    }, {});

    // Calculate Operating Thrust Load for each pump
    const operatingThrustLoadCalculations = xrefPumps.reduce<
      Record<string, { operatingThrustLoad: number }>
    >((acc, xrefPump) => {
      const pumpHydraulicCalculations = hydraulicCalculations[xrefPump.id];

      acc[xrefPump.id] = {
        operatingThrustLoad: getOperatingThrustLoad(
          xrefPump,
          pumpHydraulicCalculations?.dischargeStageCalculations
            ?.dischargePressurePSIA,
          pumpIntakePressurePSIA,
          pumpHydraulicCalculations?.intakeStageCalculations
            ?.intakeFluidPropertiesContext.fluidProperties.fluidDensityLBMFT3,
          pumpHydraulicCalculations?.dischargeStageCalculations
            ?.dischargeFluidPropertiesContext.fluidProperties
            .fluidDensityLBMFT3,
          hasCompressionPump,
          operatingRPM,
          isScenarioEspShore
        )
      };
      return acc;
    }, {});

    // Assembles all of the pumps data together in one object
    individualPumpCalculations = xrefPumps.reduce<
      PumpData["individualPumpCalculations"]
    >((acc, xrefPump) => {
      return Object.assign(acc, {
        [xrefPump.id]: {
          ...hydraulicCalculations[xrefPump.id],
          ...efficiencyCalculations[xrefPump.id],
          ...operatingThrustLoadCalculations[xrefPump.id]
        }
      });
    }, {});

    // Accumulates total BHP from all pumps
    requiredPumpBrakeHP = Object.values(hydraulicCalculations).reduce(
      (sum, calcs) => sum + calcs.totalBrakeHP,
      0
    );

    requiredIntakeBrakeHP = calculateTotalIntakeOperatingHP(
      xrefIntakes,
      fluidPropertiesAtIntake.fluidFlowRateBBLD,
      fluidPropertiesAtIntake.fluidSpecificGravity,
      frequencyRatio
    );

    const operatingThrustLoadForPrimaryPump = primaryPumpId
      ? individualPumpCalculations[primaryPumpId].operatingThrustLoad
      : 0;

    // Only perform if there is a valid top seal
    if (topSealBearingData.sealODIn > 0) {
      requiredSealBrakeHP = calculateSealOperatingHP(
        topSealBearingData.sealThrustCollarODIn,
        topSealBearingData.sealThrustCollarIDIn,
        topSealBearingData.bearingRatingAtRPMLBS,
        topSealBearingData.viscosityCp,
        operatingThrustLoadForPrimaryPump,
        frequencyRatio,
        isScenarioEspShore
      );
    }
    // Only perform if there is a valid bottom seal
    if (bottomSealBearingData.sealODIn > 0) {
      requiredSealBrakeHP += calculateSealOperatingHP(
        bottomSealBearingData.sealThrustCollarODIn,
        bottomSealBearingData.sealThrustCollarIDIn,
        bottomSealBearingData.bearingRatingAtRPMLBS,
        bottomSealBearingData.viscosityCp,
        bottomSealBearingData.sealWeightLbs,
        frequencyRatio,
        isScenarioEspShore
      );
    }

    let nextRPM = operatingRPM;

    if (motor) {
      if (motorNameplateHP !== 0) {
        motorLoadRatio =
          (requiredPumpBrakeHP + requiredIntakeBrakeHP + requiredSealBrakeHP) /
          motorNameplateHP;
      }
      if (motorLoadRatio > MAX_MOTOR_LOAD_RATIO) {
        break;
      }

      nextRPM = getMotorSpeedRPM(
        motorLoadRatio,
        pumpHz,
        motor?.nb,
        motor?.n1,
        motor?.n2,
        motor?.n3,
        motor?.n4,
        motor?.n5,
        normalizedRpmCorrection
      );
    }

    if (Math.abs(operatingRPM - nextRPM) < 0.5) {
      break;
    }

    operatingRPM = nextRPM;
    frequencyRatio = getFrequencyRatio(operatingRPM, isScenarioEspShore);
  }

  return {
    individualPumpCalculations,
    dischargePressurePSIA:
      individualPumpCalculations[xrefPumps[xrefPumps.length - 1]?.id]
        ?.dischargeStageCalculations?.dischargePressurePSIA || 0,
    dischargeTemperatureF:
      individualPumpCalculations[xrefPumps[xrefPumps.length - 1]?.id]
        ?.dischargeStageCalculations?.dischargeTemperatureF || 0,
    operatingRPM,
    requiredPumpBrakeHP,
    requiredIntakeBrakeHP,
    requiredSealBrakeHP
  };
}
